import React, { useState, useEffect } from 'react';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { enqueueSnackbar } from 'notistack';
import { ExportHubAPI, ExportLicense } from 'api/ExportHubAPI';
import { LicenseStatusPill } from './licensePresets';
import { computeCoverage } from './coverage';

interface Props {
  open: boolean;
  databaseId: string;
  productId: string;
  groupId: string | undefined;
  productDestinations: string[];
  linkedIds: string[];
  productTier?: string;
  onClose: () => void;
  onLinked: () => void;
  onRequestNew: () => void;
}

const LinkLicenseModal: React.FC<Props> = ({
  open,
  databaseId,
  productId,
  groupId,
  productDestinations,
  linkedIds,
  productTier,
  onClose,
  onLinked,
  onRequestNew,
}) => {
  const [licenses, setLicenses] = useState<ExportLicense[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [cascade, setCascade] = useState<'self' | 'children' | 'descendants'>(
    'self',
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const canCascade =
    productTier === 'Assembly' || productTier === 'Sub-assembly';

  useEffect(() => {
    if (!open || !groupId) return;
    setSelected([]);
    setCascade('self');
    setLoading(true);
    ExportHubAPI.getLicenses(databaseId, groupId)
      .then((data) => setLicenses(data))
      .catch(() => setLicenses([]))
      .finally(() => setLoading(false));
  }, [open, databaseId, groupId]);

  const toggle = (id: string) =>
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
    );

  const handleLink = async () => {
    if (!groupId || selected.length === 0) return;
    setSaving(true);
    try {
      await ExportHubAPI.linkLicenses(productId, {
        group_id: groupId,
        license_ids: selected,
        cascade: canCascade && cascade !== 'self' ? cascade : undefined,
      });
      enqueueSnackbar(
        `Linked ${selected.length} license${selected.length === 1 ? '' : 's'}.`,
        { variant: 'success' },
      );
      onLinked();
    } catch (err) {
      console.error('Failed to link licenses:', err);
      enqueueSnackbar('Failed to link licenses.', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const linkedSet = new Set(linkedIds);

  return (
    <Modal open={open} onClose={onClose} className='eh-modal-root'>
      <div className='eh-modal' role='dialog' aria-label='Link License'>
        <div className='eh-modal-head'>
          <h2 className='eh-modal-title'>Link License</h2>
          <button
            className='eh-modal-close'
            onClick={onClose}
            aria-label='Close'
            type='button'
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className='eh-modal-body'>
          <button
            className='eh-link-secondary'
            type='button'
            onClick={onRequestNew}
          >
            <AddIcon style={{ fontSize: 16 }} /> Add a new license for this
            product
          </button>

          {canCascade && (
            <div className='eh-field'>
              <span className='eh-field-label'>Apply to</span>
              <div className='eh-cascade'>
                {(
                  [
                    ['self', 'Only this product'],
                    ['children', 'This product and direct children'],
                    ['descendants', 'This product and all descendants'],
                  ] as Array<['self' | 'children' | 'descendants', string]>
                ).map(([val, label]) => (
                  <button
                    key={val}
                    type='button'
                    className={`eh-cascade-opt ${cascade === val ? 'active' : ''}`}
                    onClick={() => setCascade(val)}
                  >
                    <span className='eh-cascade-radio' />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {loading && <div className='eh-detail-note'>Loading licenses…</div>}
          {!loading && licenses.length === 0 && (
            <div className='eh-detail-note'>
              No licenses in this database yet.
            </div>
          )}

          <div className='eh-link-list'>
            {licenses.map((l) => {
              const already = linkedSet.has(l.id);
              const cov = computeCoverage(l, productDestinations);
              return (
                <button
                  key={l.id}
                  type='button'
                  className={`eh-link-row ${
                    selected.includes(l.id) ? 'sel' : ''
                  } ${already ? 'disabled' : ''}`}
                  onClick={() => !already && toggle(l.id)}
                  disabled={already}
                >
                  <span
                    className={`eh-facet-check ${
                      already || selected.includes(l.id) ? 'on' : ''
                    }`}
                  >
                    {(already || selected.includes(l.id)) && '✓'}
                  </span>
                  <span className='eh-link-main'>
                    <span className='eh-link-title'>
                      {l.license_number || l.license_type}
                      <span className='eh-link-sub'>{l.license_type}</span>
                    </span>
                    <span className='eh-link-meta'>
                      <LicenseStatusPill value={l.status} />
                      {productDestinations.length > 0 &&
                        (cov.covered ? (
                          <span className='eh-cov-ok'>Covers destinations</span>
                        ) : (
                          <span className='eh-cov-warn'>
                            Missing: {cov.missing.join(', ')}
                          </span>
                        ))}
                    </span>
                  </span>
                  {already && <span className='eh-link-tag'>Linked</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className='eh-modal-foot'>
          <button
            className='btn-outline'
            type='button'
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className='btn-primary'
            type='button'
            onClick={handleLink}
            disabled={saving || selected.length === 0}
            style={{ opacity: saving || selected.length === 0 ? 0.6 : 1 }}
          >
            {saving
              ? 'Linking…'
              : `Link ${selected.length || ''} License${
                  selected.length === 1 ? '' : 's'
                }`.trim()}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LinkLicenseModal;
